import React, { useState, useEffect, useMemo } from 'react';
import { Loader2, CheckCircle, ChevronLeft, ChevronRight, Pencil, CheckSquare, Square, Undo2, UserPlus } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { UserProfilePopover } from '@/components/ui/UserProfilePopover';
import { GuestSearchBar } from '@/components/guests/GuestSearchBar';
import {
  guestService,
  type BulkGuestEventOperation,
  type BulkGuestEventOperationResult,
} from '@/lib/api/guest.service';
import { handleApiError } from '@/lib/api/client';
import type { Event } from '@/types/event.types';
import type { GuestGroup } from '@/types/group.types';
import type { TeamRole } from '@/types/teamMember.types';
import type { GuestRow } from '@/components/guests/GuestListTab';

interface PendingChange {
  contactId: number;
  userId?: number;
  userName: string;
  eventId: number;
  action: 'add' | 'remove';
  guestId?: number;
}

interface FailedChangeNotice {
  changeKey: string;
  userName: string;
  eventId: number;
  action: 'add' | 'remove';
  retryable: boolean;
  message: string;
}

interface SaveNotice {
  level: 'partial' | 'error';
  summary: string;
  failedItems?: FailedChangeNotice[];
}

interface QuotaOverageWarning {
  eventId: number;
  eventName: string;
  hostUserId: number;
  hostName: string;
  overBy: number;
}

interface EventsTabProps {
  celebrationId: number;
  events: Event[];
  guestRows: GuestRow[];
  originalGuestRows: GuestRow[];
  groups?: GuestGroup[];
  actorRole: TeamRole | null;
  onDataChanged: () => void;
  onAddGuests?: () => void;
}

export const EventsTab: React.FC<EventsTabProps> = ({
  celebrationId,
  events,
  guestRows: externalGuestRows,
  originalGuestRows,
  groups = [],
  actorRole,
  onDataChanged,
  onAddGuests,
}) => {
  const { t } = useTranslation('guests');
  // Always start in edit mode — no view mode
  const [guestRows, setGuestRows] = useState<GuestRow[]>(externalGuestRows);
  const [pendingChanges, setPendingChanges] = useState<PendingChange[]>([]);
  const [saving, setSaving] = useState(false);
  const [showSavedMessage, setShowSavedMessage] = useState(false);
  const [saveNotice, setSaveNotice] = useState<SaveNotice | null>(null);
  const [quotaOverageWarnings, setQuotaOverageWarnings] = useState<QuotaOverageWarning[]>([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedEventIds, setSelectedEventIds] = useState<Set<number>>(new Set());
  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  // Per-event-per-guest additional guest allowance edits
  // Key format: "contactId-eventId"
  const [allowanceEdits, setAllowanceEdits] = useState<Record<string, { allowed: boolean; count: number }>>({});
  const [allowanceBase, setAllowanceBase] = useState<Record<string, { allowed: boolean; count: number }>>({});

  // Sync external guest rows when they change
  useEffect(() => {
    if (pendingChanges.length === 0) {
      setGuestRows(externalGuestRows);
    }
  }, [externalGuestRows, pendingChanges.length]);

  // Seed allowance edits from guest data
  useEffect(() => {
    const next: Record<string, { allowed: boolean; count: number }> = {};
    for (const row of externalGuestRows) {
      for (const [eventId, guest] of row.eventGuests.entries()) {
        if (guest) {
          const key = `${row.contactId}-${eventId}`;
          next[key] = {
            allowed: guest.is_additional_guest_allowed ?? false,
            count: guest.additional_guest_count ?? 0,
          };
        }
      }
    }
    setAllowanceEdits(next);
    setAllowanceBase(next);
  }, [externalGuestRows]);

  const filteredRows = useMemo(() => {
    let filtered = guestRows;

    // Filter by selected events (OR logic)
    if (selectedEventIds.size > 0) {
      filtered = filtered.filter(row => {
        for (const eventId of selectedEventIds) {
          if (row.eventGuests.has(eventId)) return true;
        }
        return false;
      });
    }

    // Text search (supports #group)
    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase();
      if (query.includes('#') && groups.length > 0) {
        // Group search with operators — same logic as GuestListTab
        const getContactIdsForGroup = (groupName: string): Set<number> => {
          const ids = new Set<number>();
          const matchingGroup = groups.find(g => g.name.toLowerCase() === groupName);
          if (!matchingGroup) return ids;
          if (matchingGroup.is_system_group) {
            if (matchingGroup.name === 'all') {
              for (const row of guestRows) ids.add(row.contactId);
            } else if (matchingGroup.name.startsWith('rsvp-')) {
              const status = matchingGroup.name.replace('rsvp-', '');
              for (const row of guestRows) {
                if (row.invitationStatus === status) ids.add(row.contactId);
              }
            }
          }
          return ids;
        };
        const segments: Array<{ groupName: string; operator: 'or' | 'and' }> = [];
        let normalized = query.replace(/\|/g, ' or ').replace(/,/g, ' or ').replace(/&/g, ' and ');
        const parts = normalized.split(/(\s+or\s+|\s+and\s+)/);
        let currentOperator: 'or' | 'and' = 'or';
        for (const part of parts) {
          const trimmed = part.trim();
          if (!trimmed) continue;
          if (/^or$/i.test(trimmed)) { currentOperator = 'or'; continue; }
          if (/^and$/i.test(trimmed)) { currentOperator = 'and'; continue; }
          const hashMatch = trimmed.match(/#([\w-]+)/);
          if (hashMatch) segments.push({ groupName: hashMatch[1], operator: currentOperator });
        }
        if (segments.length > 0) {
          let resultIds = getContactIdsForGroup(segments[0].groupName);
          for (let i = 1; i < segments.length; i++) {
            const seg = segments[i];
            const segIds = getContactIdsForGroup(seg.groupName);
            if (seg.operator === 'or') {
              for (const id of segIds) resultIds.add(id);
            } else {
              const intersection = new Set<number>();
              for (const id of resultIds) { if (segIds.has(id)) intersection.add(id); }
              resultIds = intersection;
            }
          }
          filtered = filtered.filter(row => resultIds.has(row.contactId));
        } else {
          filtered = [];
        }
      } else {
        filtered = filtered.filter(
          (row) => row.name.toLowerCase().includes(query) || row.email?.toLowerCase().includes(query)
        );
      }
    }

    return filtered;
  }, [guestRows, searchQuery, selectedEventIds, groups]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedEventIds]);

  const totalPages = Math.ceil(filteredRows.length / pageSize);
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = startIndex + pageSize;
  const paginatedRows = filteredRows.slice(startIndex, endIndex);
  const eventTitleById = useMemo(() => {
    const map = new Map<number, string>();
    for (const event of events) {
      map.set(event.id, event.title);
    }
    return map;
  }, [events]);

  const getEventGuestCount = (eventId: number): number => {
    return guestRows.filter((row) => row.eventGuests.has(eventId)).length;
  };

  const normalizeGuestCount = (value: number) => {
    if (Number.isNaN(value)) return 0;
    return Math.min(Math.max(value, 0), 10);
  };

  // Count dirty allowance edits
  const dirtyAllowanceKeys = useMemo(() => {
    return Object.keys(allowanceEdits).filter(key => {
      const edit = allowanceEdits[key];
      const base = allowanceBase[key];
      if (!base) return edit.allowed || edit.count > 0;
      return edit.allowed !== base.allowed || edit.count !== base.count;
    });
  }, [allowanceEdits, allowanceBase]);

  const totalUnsavedCount = pendingChanges.length + dirtyAllowanceKeys.length;

  const clearSaveFeedback = () => {
    setShowSavedMessage(false);
    setSaveNotice(null);
  };

  const handleCheckboxChange = (
    contactId: number,
    userId: number | undefined,
    userName: string,
    eventId: number,
    isChecked: boolean
  ) => {
    clearSaveFeedback();
    const originalRow = originalGuestRows.find((r) => r.contactId === contactId);
    const wasOriginallyChecked = originalRow?.eventGuests.has(eventId) || false;

    setGuestRows((prev) =>
      prev.map((row) => {
        if (row.contactId === contactId) {
          const newEventGuests = new Map(row.eventGuests);
          if (isChecked) {
            newEventGuests.set(eventId, null);
          } else {
            newEventGuests.delete(eventId);
          }
          return { ...row, eventGuests: newEventGuests };
        }
        return row;
      })
    );

    setPendingChanges((prev) => {
      const filtered = prev.filter(
        (c) => !(c.contactId === contactId && c.eventId === eventId)
      );

      if (isChecked === wasOriginallyChecked) return filtered;

      const existingGuest = originalRow?.eventGuests.get(eventId);

      if (isChecked && !wasOriginallyChecked) {
        return [...filtered, { contactId, userId, userName, eventId, action: 'add' as const }];
      } else if (!isChecked && wasOriginallyChecked) {
        return [...filtered, { contactId, userId, userName, eventId, action: 'remove' as const, guestId: existingGuest?.id }];
      }

      return filtered;
    });
  };

  const handleSave = async (skipQuotaWarningPrompt: boolean = false) => {
    if (pendingChanges.length === 0 && dirtyAllowanceKeys.length === 0) return;

    try {
      if (!skipQuotaWarningPrompt) {
        const overages = await collectOverAllocationWarningsForPrivilegedRole();
        if (overages.length > 0) {
          setQuotaOverageWarnings(overages);
          return;
        }
      }

      setQuotaOverageWarnings([]);
      setSaving(true);
      setSaveNotice(null);
      let remainingPendingChanges = pendingChanges;

      // --- Save invite checkbox changes ---
      if (pendingChanges.length > 0) {
        const allGuests = await guestService.getCelebrationGuests(celebrationId);
        const guestsByUserId = new Map<number, typeof allGuests>();
        for (const guest of allGuests) {
          if (!guest.user_id) continue;
          const existing = guestsByUserId.get(guest.user_id);
          if (existing) {
            existing.push(guest);
          } else {
            guestsByUserId.set(guest.user_id, [guest]);
          }
        }

        const operationsWithChanges = pendingChanges.map((change, index) => {
          const clientChangeId = `change-${index}-${change.action}-${change.contactId}-${change.eventId}`;
          let guestId = change.guestId;

          if (!guestId && change.userId !== undefined) {
            const userGuests = guestsByUserId.get(change.userId) || [];
            if (change.action === 'add') {
              const celebrationGuest = userGuests.find((guest) => !guest.event_id);
              const existingEventGuest = userGuests.find((guest) => guest.event_id === change.eventId);
              guestId = celebrationGuest?.id || existingEventGuest?.id;
            } else {
              const eventGuest = userGuests.find((guest) => guest.event_id === change.eventId);
              guestId = eventGuest?.id;
            }
          }

          const operation: BulkGuestEventOperation = {
            clientChangeId,
            action: change.action === 'add' ? 'assign' : 'unassign',
            event_id: change.eventId,
            guest_id: guestId,
            user_id: change.userId,
          };

          return { change, operation };
        });

        const operationById = new Map(
          operationsWithChanges.map((item) => [item.operation.clientChangeId, item])
        );
        const resultById = new Map<string, BulkGuestEventOperationResult>();
        const maxRetries = 2;
        let retryOperations = operationsWithChanges.map((item) => item.operation);
        let attempt = 0;

        const sleep = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));
        const isRetryableRequestError = (error: unknown): boolean => {
          if (!error || typeof error !== 'object') return false;
          const maybeAxiosError = error as { response?: { status?: number } };
          const status = maybeAxiosError.response?.status;
          if (typeof status === 'number') {
            return status === 429 || status >= 500;
          }
          return true;
        };

        while (retryOperations.length > 0 && attempt <= maxRetries) {
          try {
            const response = await guestService.bulkUpdateGuestEvents(celebrationId, retryOperations);

            for (const result of response.results) {
              resultById.set(result.clientChangeId, result);
            }

            if (attempt >= maxRetries) {
              break;
            }

            retryOperations = response.results
              .filter((result) => result.status === 'failed' && result.retryable)
              .map((result) => operationById.get(result.clientChangeId)?.operation)
              .filter((operation): operation is BulkGuestEventOperation => Boolean(operation));

            if (retryOperations.length > 0) {
              await sleep((attempt + 1) * 300);
            }
          } catch (error) {
            if (attempt < maxRetries && isRetryableRequestError(error)) {
              await sleep((attempt + 1) * 300);
              attempt += 1;
              continue;
            }
            throw error;
          }

          attempt += 1;
        }

        const failedChanges: PendingChange[] = [];
        let succeededInviteChanges = 0;
        const failedItems: FailedChangeNotice[] = [];

        for (const item of operationsWithChanges) {
          const result = resultById.get(item.operation.clientChangeId);
          if (result?.status === 'success') {
            succeededInviteChanges += 1;
            continue;
          }

          failedChanges.push(item.change);
          failedItems.push({
            changeKey: `${item.change.contactId}-${item.change.eventId}-${item.change.action}`,
            userName: item.change.userName,
            eventId: item.change.eventId,
            action: item.change.action,
            retryable: Boolean(result?.retryable),
            message: result?.message || t('eventsTab.errors.changeFailed', { userName: item.change.userName }),
          });
        }

        remainingPendingChanges = failedChanges;
        setPendingChanges(failedChanges);

        if (failedChanges.length > 0) {
          setSaveNotice({
            level: 'partial',
            summary: t('eventsTab.save.partialSummary', {
              saved: succeededInviteChanges,
              total: operationsWithChanges.length,
              failed: failedChanges.length,
            }),
            failedItems,
          });
        }
      }

      // --- Save allowance changes ---
      if (dirtyAllowanceKeys.length > 0) {
        // Re-fetch guests to get latest IDs (especially for newly added guests)
        const latestGuests = await guestService.getCelebrationGuests(celebrationId);
        const allowanceUpdates: Promise<unknown>[] = [];
        for (const key of dirtyAllowanceKeys) {
          const [contactIdStr, eventIdStr] = key.split('-');
          const eventId = parseInt(eventIdStr, 10);
          const allowance = allowanceEdits[key];
          const allowed = allowance.allowed;
          const normalizedCount = normalizeGuestCount(allowed ? Math.max(allowance.count, 1) : 0);

          // Find the guest record by contactId match and event_id
          const contactId = parseInt(contactIdStr, 10);
          const row = guestRows.find(r => r.contactId === contactId);
          const eventGuest = row?.eventGuests.get(eventId);
          // Try local eventGuest first, fall back to re-fetched data
          const guestId = eventGuest?.id || latestGuests.find(g => g.user_id === row?.userId && g.event_id === eventId)?.id;
          if (!guestId) continue;

          allowanceUpdates.push(
            guestService.updateGuest(0, guestId, {
              is_additional_guest_allowed: allowed,
              additional_guest_type: 'count',
              additional_guest_count: normalizedCount,
            })
          );
        }

        if (allowanceUpdates.length > 0) {
          await Promise.all(allowanceUpdates);
        }
      }

      if (remainingPendingChanges.length === 0) {
        setPendingChanges([]);
        setSaveNotice(null);
        setShowSavedMessage(true);
        setTimeout(() => setShowSavedMessage(false), 3000);
      }

      // Reload data from parent
      onDataChanged();
    } catch (err) {
      console.error('Error saving changes:', err);
      setSaveNotice({
        level: 'error',
        summary: handleApiError(err) || t('eventsTab.errors.saveFailed'),
      });
    } finally {
      setSaving(false);
    }
  };

  const handleProceedWithQuotaOverride = () => {
    setQuotaOverageWarnings([]);
    void handleSave(true);
  };

  const handleCancelQuotaOverride = () => {
    setQuotaOverageWarnings([]);
  };

  const handleRetryFailed = () => {
    if (saving || pendingChanges.length === 0) return;
    void handleSave();
  };

  const handleDiscardFailed = () => {
    if (saving) return;
    if (pendingChanges.length > 0 && !window.confirm(t('eventsTab.confirm.discardFailed'))) {
      return;
    }

    setPendingChanges([]);
    clearSaveFeedback();
    onDataChanged();
  };

  const handleCancel = () => {
    if (totalUnsavedCount > 0 && !window.confirm(t('eventsTab.confirm.discardUnsaved'))) {
      return;
    }
    clearSaveFeedback();
    setGuestRows([...originalGuestRows]);
    setPendingChanges([]);
    // Reset allowance edits to base
    setAllowanceEdits({ ...allowanceBase });
  };

  const toggleEventFilter = (eventId: number) => {
    setSelectedEventIds(prev => {
      const next = new Set(prev);
      if (next.has(eventId)) next.delete(eventId);
      else next.add(eventId);
      return next;
    });
  };

  const clearEventFilters = () => setSelectedEventIds(new Set());

  // Bulk actions per event column
  const selectAllForEvent = (eventId: number) => {
    clearSaveFeedback();
    setGuestRows((prev) =>
      prev.map((row) => {
        if (!row.eventGuests.has(eventId)) {
          const newEventGuests = new Map(row.eventGuests);
          newEventGuests.set(eventId, null);
          return { ...row, eventGuests: newEventGuests };
        }
        return row;
      })
    );
    // Build pending changes for all guests not originally assigned
    setPendingChanges((prev) => {
      let updated = [...prev];
      for (const row of guestRows) {
        const originalRow = originalGuestRows.find((r) => r.contactId === row.contactId);
        const wasOriginallyChecked = originalRow?.eventGuests.has(eventId) || false;
        // Remove any existing change for this guest+event
        updated = updated.filter((c) => !(c.contactId === row.contactId && c.eventId === eventId));
        if (!wasOriginallyChecked) {
          updated.push({ contactId: row.contactId, userId: row.userId, userName: row.name, eventId, action: 'add' });
        }
        // If was originally checked and we're selecting all, no change needed (matches original)
      }
      return updated;
    });
  };

  const unselectAllForEvent = (eventId: number) => {
    clearSaveFeedback();
    setGuestRows((prev) =>
      prev.map((row) => {
        if (row.eventGuests.has(eventId)) {
          const newEventGuests = new Map(row.eventGuests);
          newEventGuests.delete(eventId);
          return { ...row, eventGuests: newEventGuests };
        }
        return row;
      })
    );
    // Build pending changes for all guests originally assigned
    setPendingChanges((prev) => {
      let updated = [...prev];
      for (const row of guestRows) {
        const originalRow = originalGuestRows.find((r) => r.contactId === row.contactId);
        const wasOriginallyChecked = originalRow?.eventGuests.has(eventId) || false;
        const existingGuest = originalRow?.eventGuests.get(eventId);
        updated = updated.filter((c) => !(c.contactId === row.contactId && c.eventId === eventId));
        if (wasOriginallyChecked) {
          updated.push({ contactId: row.contactId, userId: row.userId, userName: row.name, eventId, action: 'remove', guestId: existingGuest?.id });
        }
      }
      return updated;
    });
  };

  const discardChangesForEvent = (eventId: number) => {
    clearSaveFeedback();
    // Restore original state for this event column only
    setGuestRows((prev) =>
      prev.map((row) => {
        const originalRow = originalGuestRows.find((r) => r.contactId === row.contactId);
        if (!originalRow) return row;
        const wasOriginallyChecked = originalRow.eventGuests.has(eventId);
        const isCurrentlyChecked = row.eventGuests.has(eventId);
        if (wasOriginallyChecked !== isCurrentlyChecked) {
          const newEventGuests = new Map(row.eventGuests);
          if (wasOriginallyChecked) {
            newEventGuests.set(eventId, originalRow.eventGuests.get(eventId) || null);
          } else {
            newEventGuests.delete(eventId);
          }
          return { ...row, eventGuests: newEventGuests };
        }
        return row;
      })
    );
    // Remove all pending changes for this event
    setPendingChanges((prev) => prev.filter((c) => c.eventId !== eventId));
  };

  const selectAllEventsForGuest = (contactId: number, userId: number | undefined, userName: string) => {
    if (events.length === 0) return;

    clearSaveFeedback();

    setGuestRows((prev) =>
      prev.map((row) => {
        if (row.contactId !== contactId) return row;

        const newEventGuests = new Map(row.eventGuests);
        for (const event of events) {
          if (!newEventGuests.has(event.id)) {
            newEventGuests.set(event.id, null);
          }
        }

        return { ...row, eventGuests: newEventGuests };
      })
    );

    setPendingChanges((prev) => {
      let updated = [...prev];
      const originalRow = originalGuestRows.find((r) => r.contactId === contactId);

      for (const event of events) {
        const eventId = event.id;
        const wasOriginallyChecked = originalRow?.eventGuests.has(eventId) || false;

        updated = updated.filter((c) => !(c.contactId === contactId && c.eventId === eventId));

        if (!wasOriginallyChecked) {
          updated.push({ contactId, userId, userName, eventId, action: 'add' });
        }
      }

      return updated;
    });
  };

  const getEventPendingCount = (eventId: number): number => {
    return pendingChanges.filter((c) => c.eventId === eventId).length;
  };

  const collectOverAllocationWarningsForPrivilegedRole = async (): Promise<QuotaOverageWarning[]> => {
    if (!actorRole || actorRole === 'host' || pendingChanges.length === 0) {
      return [];
    }

    const summary = await guestService.getHostQuotaSummary(celebrationId);
    const usageByKey = new Map<string, number>();
    const quotaByKey = new Map<string, number | null>();
    const hostNameById = new Map<number, string>();
    const eventNameById = new Map<number, string>();

    for (const host of summary.hosts) {
      hostNameById.set(
        host.hostUserId,
        host.hostNickname ? `${host.hostName} (${host.hostNickname})` : host.hostName
      );
    }

    for (const event of events) {
      eventNameById.set(event.id, event.title);
    }

    for (const row of summary.eventQuotas) {
      const key = `${row.eventId}:${row.hostUserId}`;
      usageByKey.set(key, row.usedSeats);
      quotaByKey.set(key, row.allocatedSeats);
    }

    for (const change of pendingChanges) {
      const row = guestRows.find((guestRow) => guestRow.contactId === change.contactId);
      const hostUserId = row?.hostUserId;

      if (!hostUserId) {
        continue;
      }

      const key = `${change.eventId}:${hostUserId}`;
      const currentUsage = usageByKey.get(key) || 0;

      let seatDelta = 0;
      if (change.action === 'add') {
        seatDelta = 1 + Math.max(row?.additionalGuestCount || 0, 0);
      } else {
        const eventGuest = row?.eventGuests.get(change.eventId);
        const existingCount = eventGuest?.additional_guest_count ?? row?.additionalGuestCount ?? 0;
        seatDelta = -(1 + Math.max(existingCount, 0));
      }

      usageByKey.set(key, currentUsage + seatDelta);
    }

    const overages: QuotaOverageWarning[] = [];
    for (const [key, projectedUsage] of usageByKey.entries()) {
      const configuredQuota = quotaByKey.get(key);
      if (configuredQuota === null || configuredQuota === undefined) {
        continue;
      }

      if (projectedUsage <= configuredQuota) {
        continue;
      }

      const [eventIdStr, hostUserIdStr] = key.split(':');
      const eventId = Number(eventIdStr);
      const hostUserId = Number(hostUserIdStr);
      const eventName = eventNameById.get(eventId) || t('eventsTab.fallback.event', { id: eventId });
      const hostName = hostNameById.get(hostUserId) || t('eventsTab.fallback.host', { id: hostUserId });
      const overBy = projectedUsage - configuredQuota;

      overages.push({
        eventId,
        eventName,
        hostUserId,
        hostName,
        overBy,
      });
    }

    return overages;
  };

  return (
    <div>
      {quotaOverageWarnings.length > 0 && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 px-4">
          <div className="w-full max-w-md rounded-lg bg-white shadow-xl">
            <div className="border-b px-6 py-4">
              <h3 className="text-lg font-semibold text-slate-900">{t('eventsTab.quotaWarning.title')}</h3>
            </div>
            <div className="space-y-3 px-6 py-4 text-sm text-slate-700">
              <p>
                {t('eventsTab.quotaWarning.primary', {
                  hostName: quotaOverageWarnings[0].hostName,
                  eventName: quotaOverageWarnings[0].eventName,
                  overBy: quotaOverageWarnings[0].overBy,
                })}
              </p>
              {quotaOverageWarnings.length > 1 && (
                <p className="text-xs text-slate-500">
                  {t('eventsTab.quotaWarning.additional', { count: quotaOverageWarnings.length - 1 })}
                </p>
              )}
            </div>
            <div className="flex justify-end gap-2 border-t px-6 py-4">
              <Button variant="outline" onClick={handleCancelQuotaOverride} disabled={saving}>
                {t('eventsTab.actions.cancel')}
              </Button>
              <Button onClick={handleProceedWithQuotaOverride} disabled={saving}>
                {t('eventsTab.actions.proceedAnyway')}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Actions Bar */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Pencil className="h-4 w-4 text-amber-600" />
          <span className="text-sm text-amber-800 font-medium">
            {actorRole === 'host'
              ? t('eventsTab.hints.host')
              : t('eventsTab.hints.organizer')}
          </span>
        </div>
        <div className="flex items-center gap-2">
          {showSavedMessage && (
            <span className="text-sm text-green-600 flex items-center gap-1">
              <CheckCircle className="w-4 h-4" /> {t('eventsTab.save.success')}
            </span>
          )}
          {totalUnsavedCount > 0 && (
            <>
              <span className="text-sm text-amber-600">
                {t('eventsTab.unsavedChanges', { count: totalUnsavedCount })}
              </span>
              <Button variant="outline" size="sm" onClick={handleCancel} disabled={saving}>
                {t('eventsTab.actions.cancel')}
              </Button>
              <Button size="sm" onClick={() => void handleSave()} disabled={saving}>
                {saving ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> {t('eventsTab.actions.saving')}</>
                ) : (
                  t('eventsTab.actions.saveChanges')
                )}
              </Button>
            </>
          )}
        </div>
      </div>

      {saveNotice && (
        <div
          className={`mb-4 rounded-md border px-4 py-3 text-sm ${
            saveNotice.level === 'error'
              ? 'border-red-200 bg-red-50 text-red-700'
              : 'border-amber-200 bg-amber-50 text-amber-800'
          }`}
        >
          <div className="font-medium">{saveNotice.summary}</div>

          {saveNotice.failedItems && saveNotice.failedItems.length > 0 && (
            <div className="mt-2 space-y-1 text-xs">
              {saveNotice.failedItems.slice(0, 5).map((item) => (
                <div key={item.changeKey} className="leading-relaxed">
                  <span className="font-semibold">{item.userName}</span>
                  <span>
                    {' '}
                    {item.action === 'add' ? t('eventsTab.actions.assign') : t('eventsTab.actions.unassign')}{' '}
                    {eventTitleById.get(item.eventId) || t('eventsTab.fallback.event', { id: item.eventId })}: {item.message}
                    {item.retryable ? ` ${t('eventsTab.retryable')}` : ''}
                  </span>
                </div>
              ))}
              {saveNotice.failedItems.length > 5 && (
                <div className="text-xs">
                  {t('eventsTab.moreFailed', { count: saveNotice.failedItems.length - 5 })}
                </div>
              )}
            </div>
          )}

          {saveNotice.failedItems && saveNotice.failedItems.length > 0 && (
            <div className="mt-3 flex items-center gap-2">
              <Button size="sm" variant="outline" onClick={handleRetryFailed} disabled={saving || pendingChanges.length === 0}>
                {t('eventsTab.actions.retryFailed')}
              </Button>
              <Button size="sm" variant="ghost" onClick={handleDiscardFailed} disabled={saving}>
                {t('eventsTab.actions.discardFailed')}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Shared Search Bar with Event Filters */}
      <GuestSearchBar
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        events={events}
        selectedEventIds={selectedEventIds}
        onToggleEvent={toggleEventFilter}
        onClearEvents={clearEventFilters}
        groups={groups}
        resultCount={filteredRows.length}
      />
      <div className="mt-2 mb-4 text-sm text-gray-600">
        {t('eventsTab.showing', {
          from: filteredRows.length > 0 ? startIndex + 1 : 0,
          to: Math.min(endIndex, filteredRows.length),
          total: filteredRows.length,
        })}
      </div>

      {/* Events Grid */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="sticky left-0 z-10 bg-gray-50 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200">
                  {t('eventsTab.columns.guestName')}
                </th>
                {events.map((event) => {
                  const eventChanges = getEventPendingCount(event.id);
                  return (
                    <th
                      key={event.id}
                      className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200"
                    >
                      <div className="flex flex-col items-center gap-1">
                        <span className="font-semibold">{event.title}</span>
                        <span className="text-[10px] text-gray-400">
                          {t('eventsTab.eventGuestCount', { current: getEventGuestCount(event.id), max: event.max_guests || '∞' })}
                        </span>
                        <div className="flex items-center gap-1 text-[9px] text-gray-400 font-normal normal-case">
                          <span>{t('eventsTab.inviteLegend')}</span>
                          <span className="text-gray-300">|</span>
                          <span>{t('eventsTab.additionalGuestLegend')}</span>
                        </div>
                        <div className="flex items-center gap-1 mt-1">
                          <button
                            onClick={() => selectAllForEvent(event.id)}
                            title={t('eventsTab.actions.selectAllForEvent')}
                            className="p-0.5 rounded hover:bg-green-100 text-gray-400 hover:text-green-600 transition-colors"
                          >
                            <CheckSquare className="w-3.5 h-3.5" />
                          </button>
                          <button
                            onClick={() => unselectAllForEvent(event.id)}
                            title={t('eventsTab.actions.unselectAllForEvent')}
                            className="p-0.5 rounded hover:bg-red-100 text-gray-400 hover:text-red-500 transition-colors"
                          >
                            <Square className="w-3.5 h-3.5" />
                          </button>
                          {eventChanges > 0 && (
                            <button
                              onClick={() => discardChangesForEvent(event.id)}
                              title={t('eventsTab.actions.discardEventChanges', { count: eventChanges })}
                              className="p-0.5 rounded hover:bg-amber-100 text-amber-500 hover:text-amber-700 transition-colors"
                            >
                              <Undo2 className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </div>
                      </div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginatedRows.length === 0 ? (
                <tr>
                  <td colSpan={events.length + 1} className="px-6 py-12 text-center text-gray-500">
                    {searchQuery ? t('eventsTab.empty.search') : t('eventsTab.empty.default')}
                    {!searchQuery && externalGuestRows.length === 0 && onAddGuests && (
                      <div className="mt-3">
                        <Button size="sm" onClick={onAddGuests}>
                          <UserPlus className="h-4 w-4 mr-1" />
                          Add guests
                        </Button>
                      </div>
                    )}
                  </td>
                </tr>
              ) : (
                paginatedRows.map((row) => {
                  // Determine if this row has any pending changes
                  const rowHasChanges = pendingChanges.some(c => c.contactId === row.contactId);

                  return (
                    <tr key={row.contactId} className={`hover:bg-gray-50 ${rowHasChanges ? 'bg-amber-50/50' : ''}`}>
                      {/* Guest Name */}
                      <td className={`sticky left-0 z-10 px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200 ${rowHasChanges ? 'bg-amber-50/50' : 'bg-white'}`}>
                        <div className="flex items-center justify-between gap-3">
                          <UserProfilePopover
                            celebrationId={celebrationId}
                            rsvpContext={events.filter(event => row.eventGuests.get(event.id)).map(event => ({ eventId: event.id, eventTitle: event.title, status: row.eventGuests.get(event.id)?.rsvp_status }))}
                            user={{
                              id: row.userId || 0,
                              full_name: row.name,
                              email: row.email || '',
                              phone: row.phone,
                            }}
                          >
                            <span className="hover:underline cursor-pointer">
                              {row.name}
                              {row.nickname && (
                                <span className="text-xs text-gray-500 ml-1">({row.nickname})</span>
                              )}
                            </span>
                          </UserProfilePopover>

                          <button
                            type="button"
                            onClick={() => selectAllEventsForGuest(row.contactId, row.userId, row.name)}
                            className="text-[10px] font-semibold text-blue-600 hover:text-blue-700 hover:underline"
                            title={t('eventsTab.actions.selectAllEventsForGuest')}
                          >
                            {t('eventsTab.actions.allEvents')}
                          </button>
                        </div>
                      </td>

                      {/* Event Checkboxes + Additional Guest Allowance */}
                      {events.map((event) => {
                        const isInvited = row.eventGuests.has(event.id);
                        const originalRow = originalGuestRows.find(r => r.contactId === row.contactId);
                        const wasOriginallyInvited = originalRow?.eventGuests.has(event.id) || false;
                        const isChanged = isInvited !== wasOriginallyInvited;
                        const allowanceKey = `${row.contactId}-${event.id}`;
                        const allowance = allowanceEdits[allowanceKey] || { allowed: false, count: 0 };
                        const aBase = allowanceBase[allowanceKey] || { allowed: false, count: 0 };
                        const allowanceDirty = allowance.allowed !== aBase.allowed || allowance.count !== aBase.count;

                        return (
                          <td
                            key={event.id}
                            className={`px-3 py-3 text-center border-r border-gray-200 ${
                              isChanged ? 'bg-amber-100' : ''
                            }`}
                          >
                            <div className="flex items-center justify-center gap-2">
                              <input
                                type="checkbox"
                                title={t('eventsTab.actions.assignGuestToEvent', { name: row.name, event: event.title })}
                                checked={isInvited}
                                onChange={(e) =>
                                  handleCheckboxChange(
                                    row.contactId,
                                    row.userId,
                                    row.name,
                                    event.id,
                                    e.target.checked
                                  )
                                }
                                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                              />
                              {/* Per-event additional guest allowance — always reserve space */}
                              <span className={`text-[10px] text-gray-400 ${isInvited ? '' : 'invisible'}`}>+G</span>
                              <input
                                type="number"
                                title={t('eventsTab.actions.additionalSeatsForGuest', { name: row.name, event: event.title })}
                                placeholder={t('eventsTab.values.zero')}
                                min={0}
                                max={10}
                                value={isInvited ? allowance.count : 0}
                                onChange={(e) => {
                                  clearSaveFeedback();
                                  const val = normalizeGuestCount(parseInt(e.target.value, 10));
                                  setAllowanceEdits(prev => ({
                                    ...prev,
                                    [allowanceKey]: {
                                      allowed: val > 0,
                                      count: val,
                                    },
                                  }));
                                }}
                                disabled={!isInvited}
                                className={`w-10 h-5 text-[10px] border rounded px-1 text-center ${isInvited ? (allowanceDirty ? 'border-amber-400 bg-amber-50' : 'border-gray-300') : 'invisible border-gray-300'}`}
                              />
                            </div>
                          </td>
                        );
                      })}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-gray-50 px-6 py-3 border-t border-gray-200 flex items-center justify-between">
            <div className="text-sm text-gray-600">
              {t('eventsTab.pagination.page', { current: currentPage, total: totalPages })}
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.max(1, p - 1))} disabled={currentPage === 1}>
                <ChevronLeft className="h-4 w-4 mr-1" /> {t('eventsTab.pagination.previous')}
              </Button>
              <Button variant="outline" size="sm" onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))} disabled={currentPage === totalPages}>
                {t('eventsTab.pagination.next')} <ChevronRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
